import { Euler, Quaternion, Vector3 } from 'three';

import type { EventManager } from '../event-manager.class';
import type { SelectionResult } from '../types';
import { findObjectWithId } from '../helpers/find-object-with-id.helper';
import { isNonInteractiveObjectType, isOverlayObjectType } from '../../../../interaction';

export interface RaycastParams {
	screenX: number;
	screenY: number;
}

/**
 * Perform a raycast at the given screen coordinates
 */
export const raycastCfn = (self: EventManager, params: RaycastParams): SelectionResult | null => {
	const { screenX, screenY } = params;
	const config = self.internalConfig;
	const { domElement, scene, camera } = config;
	const rect = domElement.getBoundingClientRect();

	// Calculate normalized device coordinates
	const pointer = self.internalPointer;
	pointer.x = ((screenX - rect.left) / rect.width) * 2 - 1;
	pointer.y = -((screenY - rect.top) / rect.height) * 2 + 1;

	// Update raycaster
	const raycaster = self.internalRaycaster;
	raycaster.setFromCamera(pointer, camera);

	// Find intersections
	const intersects = raycaster.intersectObjects(scene.children, true);

	if (intersects.length === 0) {
		return null;
	}

	// Overlays win over anything geometrically nearer, and non-interactive types are looked straight
	// through — both sets come from the registry so a consumer can add its own without editing this.
	for (const intersect of intersects) {
		const hit = findObjectWithId(intersect.object);
		if (isOverlayObjectType(hit?.userData?.type)) {
			const worldQuaternion = new Quaternion();
			hit.getWorldQuaternion(worldQuaternion);
			const worldRotation = new Euler().setFromQuaternion(worldQuaternion);
			return {
				objectId: hit.userData.id,
				object: hit,
				point: intersect.point.clone(),
				normal: null,
				worldRotation
			};
		}
	}

	for (const intersect of intersects) {
		const result = findObjectWithId(intersect.object);
		if (result && !isNonInteractiveObjectType(result.userData?.type)) {
			// Calculate face normal in world space
			let normal: Vector3 | null = null;
			if (intersect.face) {
				normal = intersect.face.normal.clone();
				normal.transformDirection(intersect.object.matrixWorld).normalize();
			}

			// Get world rotation
			const worldQuaternion = new Quaternion();
			result.getWorldQuaternion(worldQuaternion);
			const worldRotation = new Euler().setFromQuaternion(worldQuaternion);

			return {
				objectId: result.userData.id,
				object: result,
				point: intersect.point.clone(),
				normal,
				worldRotation
			};
		}
	}

	return null;
};
